/**
 * The Overview's hero picture, read from live data (the marketing site's miniature is drawn on the
 * same shape): projects hand work to agents, agents submit to the landing queue, the queue lands on
 * main. Pure, so it is tested; the components only lay it out.
 */
import type { FeedEvent, Id, Incident, Landing, Pipeline, PipelineItem, Session } from '@orgus/protocol';
import { agentKindLabel, agentKindOf, pipelineKey } from '@orgus/protocol';
import { deliveryOf, inWindow, nameable, projectNameOf } from './flow.ts';
import type { FlowInput } from './flow.ts';

/** How one live session reads on its card: blocked on a person, working, asked or idle. */
export type HeroAgentState = 'needs' | 'working' | 'asked' | 'idle';

export interface HeroProject {
  id: Id;
  name: string;
  sessions: number;
  needs: number;
  working: number;
  /** This project's own main branch. */
  main: HeroMain;
}

export interface HeroAgent {
  /** The session id. */
  id: Id;
  projectId: Id | null;
  kind: string;
  name: string;
  task: string;
  state: HeroAgentState;
}

export type HeroQueueState = 'verifying' | 'queued' | 'parked';

export interface HeroQueueRow {
  id: Id;
  /** "#422" for a pull request, else the change's title. */
  label: string;
  title: string;
  state: HeroQueueState;
  /** 0 to 1 while verifying with a known average, else null. */
  progress: number | null;
  projectId: Id;
  projectName: string;
  /** The session that submitted it, when known: its dot leaves that agent's card. */
  sessionId: Id | null;
  href: string;
}

export type HeroMainState = 'green' | 'red' | 'held' | 'quiet';

export interface HeroMain {
  /** The project whose main this is; null for an incident about main that names no project. */
  projectId: Id | null;
  name: string;
  state: HeroMainState;
  /** Short sha of the last landed commit, when known. */
  sha: string | null;
  /** What landed last ("#420" or its title). */
  last: string | null;
  lastAt: number | null;
  /** Why it is red or held, in words. */
  why: string | null;
  href: string;
}

/** Every listed project's main at once: how many are green, red, held or quiet, the problems first. */
export interface HeroMainRollup {
  total: number;
  green: number;
  red: number;
  held: number;
  quiet: number;
  /** Red, then held, then green, then quiet: the order the Main column lists them in. */
  mains: HeroMain[];
}

export interface HeroModel {
  projects: HeroProject[];
  moreProjects: number;
  agents: HeroAgent[];
  moreAgents: number;
  /** Live sessions outside the watched projects (QA-40): counted, not pictured. */
  external: number;
  queue: { rows: HeroQueueRow[]; total: number; verifying: number; queued: number; parked: number; more: number };
  mains: HeroMainRollup;
}

export interface HeroCounts {
  /** Live sessions working now. */
  working: number;
  live: number;
  idle: number;
  /** Changes queued or verifying across the landing queue and every pipeline. */
  inQueue: number;
  verifying: number;
  queued: number;
  landedToday: number;
  failedToday: number;
}

type MainIncident = Pick<Incident, 'id' | 'state' | 'scope' | 'title'> & { detector?: Incident['detector'] };

const READ_RANK: Record<HeroAgentState, number> = { needs: 0, working: 1, asked: 2, idle: 3 };
const MAIN_RANK: Record<HeroMainState, number> = { red: 0, held: 1, green: 2, quiet: 3 };

function agentState(s: Session, needs: FlowInput['needs']): HeroAgentState {
  const level = needs?.get(s.id);
  if (level === 'blocked') return 'needs';
  if (level === 'asked') return 'asked';
  if (!needs && s.status === 'waiting') return 'needs';
  return s.status === 'working' ? 'working' : 'idle';
}

/** The tile counts: the same delivery sources the flow reads, so the tiles and the picture agree. */
export function heroCounts(input: FlowInput): HeroCounts {
  const d = deliveryOf(input);
  let working = 0;
  let live = 0;
  let idle = 0;
  for (const s of input.sessions) {
    if (s.status === 'ended') continue;
    live++;
    if (s.status === 'working') working++;
    else if (s.status === 'idle') idle++;
  }
  // R-FN-8: a mission Orgus landed itself (worktree.integrate) reached main too, as the Main branch card says.
  const now = input.now ?? Date.now();
  const integrated = (input.missions ?? []).filter((m) => inWindow(m.landedAt, now)).length;
  return { working, live, idle, inQueue: d.queued + d.verifying, verifying: d.verifying, queued: d.queued, landedToday: d.landed + integrated, failedToday: d.failed };
}

const short = (sha: string | null | undefined) => (sha ? sha.slice(0, 7) : null);
const landingLabel = (l: Pick<Landing, 'host' | 'title'>) => (l.host?.number ? `#${l.host.number}` : l.title);
const openIncident = (i: MainIncident) => i.state === 'open' || i.state === 'acknowledged' || i.state === 'fixing';

function queueRows(input: FlowInput, now: number): HeroQueueRow[] {
  const rows: HeroQueueRow[] = [];
  const name = (id: Id) => projectNameOf(input, id);
  const fromLanding = (l: Landing, state: HeroQueueState, progress: number | null): HeroQueueRow => ({
    id: l.id,
    label: landingLabel(l),
    title: l.title,
    state,
    progress,
    projectId: l.projectId,
    projectName: name(l.projectId),
    sessionId: l.sessionId ?? null,
    href: `/pipelines/landing?landing=${encodeURIComponent(l.id)}`,
  });
  const landing = input.landing;
  if (landing) {
    const byId = new Map(landing.landings.map((l) => [l.id, l]));
    for (const q of landing.queues) {
      const active = q.activeId ? byId.get(q.activeId) : undefined;
      if (active && active.status === 'active') {
        const started = active.startedAt ?? active.updatedAt;
        rows.push(fromLanding(active, 'verifying', q.avgMs ? Math.max(0.04, Math.min(0.96, (now - started) / q.avgMs)) : null));
      }
    }
    for (const q of landing.queues)
      for (const id of q.order) {
        const l = byId.get(id);
        if (l && (l.status === 'queued' || l.status === 'parked')) rows.push(fromLanding(l, l.status === 'parked' ? 'parked' : 'queued', null));
      }
    // Parked landings leave the order: they still wait on a person.
    for (const l of landing.landings) if (l.status === 'parked' && !rows.some((r) => r.id === l.id)) rows.push(fromLanding(l, 'parked', null));
  }
  for (const p of input.pipelines) {
    const href = `/pipelines?pipeline=${encodeURIComponent(pipelineKey(p))}`;
    const item = (i: PipelineItem, state: HeroQueueState): HeroQueueRow => ({ id: `${pipelineKey(p)}:${i.id}`, label: i.title, title: i.title, state, progress: null, projectId: p.projectId, projectName: name(p.projectId), sessionId: null, href });
    for (const i of p.items) if (i.state === 'active') rows.push(item(i, 'verifying'));
    for (const i of p.items) if (i.state === 'queued') rows.push(item(i, 'queued'));
  }
  const rank: Record<HeroQueueState, number> = { verifying: 0, parked: 1, queued: 2 };
  return rows.map((r, i) => [r, i] as const).sort((a, b) => rank[a[0].state] - rank[b[0].state] || a[1] - b[1]).map(([r]) => r);
}

/**
 * One project's main: red when the watch has an open incident about that project's main, held
 * when one of its pipelines holds its landings, green once something landed on it, else quiet.
 */
export function mainHealth(input: FlowInput, projectId: Id, incidents: readonly MainIncident[] = []): HeroMain {
  const red = incidents.find((i) => i.scope.main && i.scope.projectId === projectId && openIncident(i));
  let last: { at: number; sha: string | null; label: string } | null = null;
  for (const l of input.landing?.landings ?? [])
    if (l.projectId === projectId && l.status === 'landed' && l.endedAt && (!last || l.endedAt > last.at)) last = { at: l.endedAt, sha: short(l.landedSha), label: landingLabel(l) };
  const mine = (input.pipelines as readonly Pipeline[]).filter((p) => p.projectId === projectId);
  for (const p of mine) for (const i of p.recent) if (i.state === 'passed' && i.endedAt && (!last || i.endedAt > last.at)) last = { at: i.endedAt, sha: null, label: i.title };
  // A mission Orgus landed itself (worktree.integrate) lands on main without the landing queue (QA-42).
  for (const m of input.missions ?? []) if (m.projectId === projectId && m.landedAt && (!last || m.landedAt > last.at)) last = { at: m.landedAt, sha: null, label: 'Mission landed' };
  const held = mine.find((p) => p.hold);
  const base = { projectId, name: projectNameOf(input, projectId), sha: last?.sha ?? null, last: last?.label ?? null, lastAt: last?.at ?? null };
  if (red) return { ...base, state: 'red', why: red.title, href: `/?incident=${encodeURIComponent(red.id)}` };
  if (held) return { ...base, state: 'held', why: held.hold ?? null, href: `/pipelines?pipeline=${encodeURIComponent(pipelineKey(held))}` };
  return { ...base, state: last ? 'green' : 'quiet', why: null, href: '/pipelines/landing' };
}

/** The watched projects and those with anything going on: a live session, a mission, a landing (not cancelled), or a pipeline. */
function activeProjectIds(input: FlowInput): Id[] {
  // Every watched project, then any other a session, landing or pipeline names (QA-42: watched
  // projects with nothing live left the column empty).
  const ids = new Set<Id>(Object.keys(input.projects));
  for (const m of input.missions ?? []) ids.add(m.projectId);
  for (const s of input.sessions) if (s.status !== 'ended' && s.projectId) ids.add(s.projectId);
  for (const l of input.landing?.landings ?? []) if (l.status !== 'cancelled') ids.add(l.projectId);
  for (const p of input.pipelines) ids.add(p.projectId);
  // A project removed with its machine is gone: its old missions and landings never make an "Unknown project" card (R-FN-8).
  return [...ids].filter((id) => nameable(input, id));
}

/** Every listed project's main, the problems first. */
export function rollupMains(mains: readonly HeroMain[]): HeroMainRollup {
  const n = (s: HeroMainState) => mains.filter((m) => m.state === s).length;
  return { total: mains.length, green: n('green'), red: n('red'), held: n('held'), quiet: n('quiet'), mains: [...mains].sort((a, b) => MAIN_RANK[a.state] - MAIN_RANK[b.state] || a.name.localeCompare(b.name)) };
}

/** "1 red, 5 green": the rollup in words, problems first, zero parts left out. */
export function rollupText(r: Pick<HeroMainRollup, 'green' | 'red' | 'held' | 'quiet'>): string {
  return [r.red && `${r.red} red`, r.held && `${r.held} held`, r.green && `${r.green} green`, r.quiet && `${r.quiet} nothing landed`].filter(Boolean).join(', ') || 'No mains yet';
}

/**
 * The picture: at most `max.projects` projects and `max.agents` agents, what needs you first. A
 * project whose main is red or held, or with an agent that needs you, is never folded into
 * "+N more": the list grows past the cap for it.
 */
export function buildHero(input: FlowInput, incidents: readonly MainIncident[] = [], max = { projects: 4, agents: 5, queue: 4 }): HeroModel {
  const now = input.now ?? Date.now();
  const all0 = input.sessions.filter((s) => s.status !== 'ended');
  // QA-40: with projects watched, the picture is theirs; sessions elsewhere on the machine (other
  // repos, other agents) are counted as external, not shown as this org's work.
  const watched = Object.keys(input.projects).length > 0;
  const ours = (s: Session) => !watched || (!!s.projectId && !!input.projects[s.projectId]);
  const live = all0.filter(ours);
  const external = all0.length - live.length;
  const reads = live.map((s) => ({ s, state: agentState(s, input.needs) }));
  reads.sort((a, b) => READ_RANK[a.state] - READ_RANK[b.state] || b.s.lastActivity - a.s.lastActivity);
  // What needs you leads, but at most two of it: the picture shows the work moving too.
  const needsFirst = reads.filter((x) => x.state === 'needs').slice(0, 2);
  const picked = [...needsFirst, ...reads.filter((x) => x.state !== 'needs'), ...reads.filter((x) => x.state === 'needs' && !needsFirst.includes(x))].slice(0, max.agents);
  const agents: HeroAgent[] = picked.map(({ s, state }) => {
    const kind = agentKindOf(s);
    return { id: s.id, projectId: s.projectId ?? null, kind, name: agentKindLabel(kind), task: s.title || 'Untitled session', state };
  });

  const projects = new Map<Id, HeroProject>();
  for (const id of activeProjectIds(input)) projects.set(id, { id, name: projectNameOf(input, id), sessions: 0, needs: 0, working: 0, main: mainHealth(input, id, incidents) });
  for (const { s, state } of reads) {
    const p = s.projectId ? projects.get(s.projectId) : undefined;
    if (!p) continue;
    p.sessions++;
    if (state === 'needs') p.needs++;
    if (state === 'working') p.working++;
  }
  const shownIn = new Set(agents.map((a) => a.projectId));
  const problem = (p: HeroProject) => (p.main.state === 'red' ? 0 : p.main.state === 'held' ? 1 : p.needs > 0 ? 2 : 3);
  const all = [...projects.values()].sort(
    (a, b) => problem(a) - problem(b) || Number(shownIn.has(b.id)) - Number(shownIn.has(a.id)) || b.working - a.working || b.sessions - a.sessions || a.name.localeCompare(b.name),
  );
  const shown = all.filter((p, i) => i < max.projects || problem(p) < 3);

  // An incident about main that names no project (an older hub): its own entry in the rollup, never dropped.
  const loose = incidents.filter((i) => i.scope.main && !i.scope.projectId && i.detector === 'main-red' && openIncident(i));
  const unowned: HeroMain[] = loose.slice(0, 1).map((i) => ({ projectId: null, name: 'Main', state: 'red', sha: null, last: null, lastAt: null, why: i.title, href: `/?incident=${encodeURIComponent(i.id)}` }));

  const rows = queueRows(input, now);
  return {
    projects: shown,
    moreProjects: all.length - shown.length,
    agents,
    moreAgents: Math.max(0, live.length - agents.length),
    external,
    queue: {
      rows: rows.slice(0, max.queue),
      total: rows.length,
      verifying: rows.filter((r) => r.state === 'verifying').length,
      queued: rows.filter((r) => r.state === 'queued').length,
      parked: rows.filter((r) => r.state === 'parked').length,
      more: Math.max(0, rows.length - max.queue),
    },
    mains: rollupMains([...unowned, ...all.map((p) => p.main)]),
  };
}

/** Times bucketed into `count` slots over the window ending now, oldest first: a tile's sparkline. */
export function buckets(times: Iterable<number>, now: number, windowMs: number, count = 12): number[] {
  const out = Array.from({ length: count }, () => 0);
  const start = now - windowMs;
  const size = windowMs / count;
  for (const at of times) {
    if (at <= start || at > now) continue;
    out[Math.min(count - 1, Math.floor((at - start) / size))]! += 1;
  }
  return out;
}

/** A running total of `buckets`: landed so far today climbs. */
export function cumulative(values: readonly number[]): number[] {
  let n = 0;
  return values.map((v) => (n += v));
}

/** Landed today, by hour so far, from the landing queue and the pipelines: the Landed today sparkline. */
export function landedTimes(input: FlowInput, now: number): number[] {
  const out: number[] = [];
  for (const l of input.landing?.landings ?? []) if (l.status === 'landed' && inWindow(l.endedAt, now)) out.push(l.endedAt!);
  for (const p of input.pipelines) for (const i of p.recent) if (i.state === 'passed' && inWindow(i.endedAt, now)) out.push(i.endedAt!);
  for (const m of input.missions ?? []) if (inWindow(m.landedAt, now)) out.push(m.landedAt!);
  return out;
}

/** Submissions to the landing queue (and pipelines) by time: the In the queue sparkline. */
export function submittedTimes(input: FlowInput): number[] {
  const out: number[] = [];
  for (const l of input.landing?.landings ?? []) out.push(l.submittedAt);
  for (const p of input.pipelines) for (const i of [...p.items, ...p.recent]) if (i.startedAt) out.push(i.startedAt);
  return out;
}

const OUTCOME_TITLE = /^(queued to land|submitted|landed|landing failed|needs you|failed|deploy|deployed|promoted|rolled back)\b/i;

/**
 * Outcomes only (the Activity default): something was submitted, landed, failed or needs you, a
 * deploy moved, a budget or security alert fired, a machine went offline. Everything else
 * (pushes, subagents, jobs that passed) stays under All activity.
 */
export function isOutcome(e: Pick<FeedEvent, 'kind'> & { title?: string }): boolean {
  const title = e.title ?? '';
  if (e.kind === 'session.waiting' || e.kind === 'cost.alert' || e.kind === 'security.alert' || e.kind === 'machine.offline') return true;
  if (e.kind === 'pipeline.item') return OUTCOME_TITLE.test(title) || /\bfailed\b/i.test(title);
  if (e.kind === 'job' || e.kind === 'workflow.finished') return /\bfailed\b/i.test(title);
  return false;
}
